import { AlertTriangle, Loader2, RefreshCw, RotateCcw, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  failureMessageFor,
  inFlightMessageFor,
  reachMessageFor,
  recoveryVerbsFor,
  type PostingRecoveryView,
  type RecoveryVerb,
} from './postingRecovery';

/**
 * N8 (86d3w2kez) part 2 — the reach-aware recovery core, rendered by every posting surface.
 *
 * <b>One component, five mount points:</b> the SO card, the CL card, the MIS batch banner, the IR
 * banner, and the floating panel a deep-linked arrival raises. Before this, four of those had their
 * own copy of the same logic and their own wording for the same situation, and two hand-rolled the
 * reach comparison rather than calling the shared predicate. The button set and the copy are now
 * decided in `postingRecovery.ts` and drawn here — so "the panel and the inline card render an
 * identical button set for a given reach" is true by construction rather than by review.
 *
 * <b>Chrome is the caller's.</b> This renders content only — no Card, no border, no positioning. The
 * CL card is edge-to-edge inside a panel, the SO card is a normal card, the MIS banner stacks per
 * batch and the floating panel is fixed to the viewport. Baking any of that in here would force three
 * of the five to fight it.
 */
export function PostingRecoveryContent({
  view,
  onRetry,
  onReconcile,
  onCancel,
  isBusy = false,
  className,
}: {
  view: PostingRecoveryView;

  /**
   * Re-post from scratch. Offered ONLY when nothing reached NetSuite.
   *
   * ⚠️ MIS and IR pass the SAME function for retry and reconcile, and that is correct rather than
   * lazy: on those flows one server-side re-arm serves both, and the adopt probe branches internally.
   * What must differ is the LABEL, which is what tells the user which of the two they are doing.
   */
  onRetry?: () => void;
  /** Re-arm so the frozen decision re-executes and adopts the record NetSuite already holds. */
  onReconcile?: () => void;
  /** Close a never-reached failure off without re-posting. Omit where the flow has no such verb. */
  onCancel?: (() => void) | null;
  isBusy?: boolean;
  className?: string;
}) {
  if (view.status === 'Posting') {
    return (
      <div className={cn('flex items-center gap-3', className)}>
        <Loader2 className="h-5 w-5 shrink-0 animate-spin text-info" aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-info">
            Posting to NetSuite…
            {view.subLabel && <span className="font-normal"> · {view.subLabel}</span>}
          </p>
          <p className="text-xs text-muted-foreground">{inFlightMessageFor(view)}</p>
        </div>
      </div>
    );
  }

  const verbs = recoveryVerbsFor(view, onCancel != null);
  const reachMessage = reachMessageFor(view);

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-destructive">
            Posting failed
            {view.subLabel && <span className="font-normal text-foreground"> · {view.subLabel}</span>}
          </p>
          <p className="mt-0.5 text-sm text-foreground">{failureMessageFor(view)}</p>
        </div>
      </div>

      {/* The disclosure that stops a duplicate. Rendered only when there is genuinely something over
          there to disclose — see `reachMessageFor`. */}
      {reachMessage && (
        <p className="text-sm font-medium text-warning">⚠️ {reachMessage}</p>
      )}

      <div className="flex flex-wrap gap-2">
        {verbs.map((verb) => (
          <VerbButton
            key={verb}
            verb={verb}
            isBusy={isBusy}
            onClick={handlerFor(verb, { onRetry, onReconcile, onCancel })}
          />
        ))}
      </div>
    </div>
  );
}

function handlerFor(
  verb: RecoveryVerb,
  handlers: {
    onRetry?: () => void;
    onReconcile?: () => void;
    onCancel?: (() => void) | null;
  },
): (() => void) | undefined {
  switch (verb) {
    case 'retry':
      return handlers.onRetry;
    case 'reconcile':
      // Falls back to onRetry on purpose: MIS and IR have one re-arm endpoint serving both verbs, so
      // they pass only `onRetry` and rely on this to label it correctly.
      return handlers.onReconcile ?? handlers.onRetry;
    case 'cancel':
      return handlers.onCancel ?? undefined;
  }
}

/**
 * One verb, one button — with the wording and the emphasis that verb always carries.
 *
 * Reconcile is the PRIMARY button wherever it appears, because on a reached failure it is the only
 * safe action in the product and the user should not have to work out which of several buttons to
 * press.
 */
function VerbButton({
  verb,
  isBusy,
  onClick,
}: {
  verb: RecoveryVerb;
  isBusy: boolean;
  onClick?: () => void;
}) {
  if (!onClick) return null;

  const { label, Icon, variant } = VERB_UI[verb];

  return (
    <Button size="sm" variant={variant} onClick={onClick} disabled={isBusy}>
      {isBusy ? (
        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <Icon className="mr-1.5 h-4 w-4" aria-hidden="true" />
      )}
      {label}
    </Button>
  );
}

const VERB_UI: Record<
  RecoveryVerb,
  { label: string; Icon: typeof RotateCcw; variant: 'default' | 'outline' }
> = {
  retry: { label: 'Retry', Icon: RotateCcw, variant: 'default' },
  reconcile: { label: 'Reconcile', Icon: RefreshCw, variant: 'default' },
  cancel: { label: 'Cancel post', Icon: XCircle, variant: 'outline' },
};
